/* ============================================================
   Dathent — Public referral signup (/r/:code)
   Lead capture, NO password. Stores referral_signups row.
   Source: design/Referral Signup.html
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import Turnstile from '../components/Turnstile.jsx';
import ThemeToggle from '../components/AppShell/ThemeToggle.jsx';

const COPY = {
  en: {
    have: 'Already have an account?',
    signIn: 'Sign in',
    eyebrow: 'Personal invitation',
    headPre: 'thinks Dathent would',
    headAccent: 'work for you.',
    formTitle: 'Create your account',
    formSub: 'Set up your workspace in under a minute.',
    nameL: 'Your name',
    namePh: 'Your name',
    emailL: 'Email',
    emailPh: 'you@yourstudio.com',
    passwordL: 'Password',
    passwordPh: 'At least 8 characters',
    passwordMin: 'Password must be at least 8 characters',
    completeVerification: 'Please complete the verification below',
    inviteOnly: "Self-serve signup isn't open yet — please check back soon.",
    cta: 'Create account',
    submitting: 'Creating account…',
    foot: ['By creating an account you agree to our ', 'terms', ' and ', 'privacy policy', '.'],
    bonus: 'Invited by a friend',
    perks: [
      { l: 'Invited by someone who uses Dathent', s: 'A personal recommendation, not a cold ad.' },
      { l: 'Plan and ship content for your brand', s: 'Onboard your business and build your first calendar.' },
      { l: 'Set up in minutes', s: 'No long forms — start creating right away.' },
    ],
    notFound: 'Referral link not found',
    errorGeneric: 'Could not create your account, please try again',
  },
  uk: {
    have: 'Вже маєш аккаунт?',
    signIn: 'Увійти',
    eyebrow: 'Особисте запрошення',
    headPre: 'вважає, що Dathent',
    headAccent: 'тобі підійде.',
    formTitle: 'Створи акаунт',
    formSub: 'Налаштуй робочий простір менш ніж за хвилину.',
    nameL: 'Ім\'я',
    namePh: 'Твоє ім\'я',
    emailL: 'Email',
    emailPh: 'you@yourstudio.com',
    passwordL: 'Пароль',
    passwordPh: 'Щонайменше 8 символів',
    passwordMin: 'Пароль має містити щонайменше 8 символів',
    completeVerification: 'Будь ласка, пройди перевірку нижче',
    inviteOnly: 'Самостійна реєстрація поки закрита — зазирни трохи згодом.',
    cta: 'Створити акаунт',
    submitting: 'Створюю акаунт…',
    foot: ['Реєструючись, ти погоджуєшся з нашими ', 'умовами', ' та ', 'політикою приватності', '.'],
    bonus: 'Запрошення від друга',
    perks: [
      { l: 'Тебе запросив той, хто користується Dathent', s: 'Особиста рекомендація, а не холодна реклама.' },
      { l: 'Плануй і випускай контент для свого бренду', s: 'Підключи бізнес і збери перший календар.' },
      { l: 'Налаштування за хвилини', s: 'Без довгих форм — починай творити одразу.' },
    ],
    notFound: 'Реферальне посилання не знайдено',
    errorGeneric: 'Не вдалося створити акаунт, спробуй ще раз',
  },
  ru: {
    have: 'Уже есть аккаунт?',
    signIn: 'Войти',
    eyebrow: 'Личное приглашение',
    headPre: 'считает, что Dathent',
    headAccent: 'тебе подойдёт.',
    formTitle: 'Создай аккаунт',
    formSub: 'Настрой рабочее пространство меньше чем за минуту.',
    nameL: 'Имя',
    namePh: 'Ваше имя',
    emailL: 'Email',
    emailPh: 'you@yourstudio.com',
    passwordL: 'Пароль',
    passwordPh: 'Минимум 8 символов',
    passwordMin: 'Пароль должен быть не короче 8 символов',
    completeVerification: 'Пожалуйста, пройдите проверку ниже',
    inviteOnly: 'Самостоятельная регистрация пока закрыта — загляните чуть позже.',
    cta: 'Создать аккаунт',
    submitting: 'Создаю аккаунт…',
    foot: ['Регистрируясь, вы соглашаетесь с нашими ', 'условиями', ' и ', 'политикой приватности', '.'],
    bonus: 'Приглашение от друга',
    perks: [
      { l: 'Вас пригласил тот, кто пользуется Dathent', s: 'Личная рекомендация, а не холодная реклама.' },
      { l: 'Планируйте и публикуйте контент для бренда', s: 'Подключите бизнес и соберите первый календарь.' },
      { l: 'Настройка за минуты', s: 'Без длинных форм — начните создавать сразу.' },
    ],
    notFound: 'Реферальная ссылка не найдена',
    errorGeneric: 'Не удалось создать аккаунт, попробуйте ещё раз',
  },
};

function detectLocale() {
  if (typeof navigator === 'undefined') return 'en';
  const lang = (navigator.language || 'en').slice(0, 2).toLowerCase();
  if (['uk', 'ru', 'en'].includes(lang)) return lang;
  return 'en';
}

const DEFAULT_GRADIENT = ['#D9CBB3', '#766858'];

export function ReferralSignup() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [locale, setLocale] = useState(detectLocale());
  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(null);
  const [tsReset, setTsReset] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { t } = useTranslation();
  const { reloadUser } = useAuth?.() || {};
  const tr = COPY[locale] || COPY.en;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await api.referral.lookupByCode(code);
        if (cancelled) return;
        if (result?.referrer) {
          setReferrer(result.referrer);
          sessionStorage.setItem('dathent.referralCode', code);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        if (err?.status === 404) setNotFound(true);
        else setError(tr.errorGeneric);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const utm = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const out = {};
    for (const [k, v] of params.entries()) {
      if (k.startsWith('utm_')) out[k] = v;
    }
    return Object.keys(out).length ? out : null;
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.includes('@')) { setError(tr.emailL + ' ' + t('referralSignup.invalid')); return; }
    if (!password || password.length < 8) { setError(tr.passwordMin); return; }
    if (!token) { setError(tr.completeVerification); return; }
    setSubmitting(true);
    try {
      // 1. Record the referral attribution row (non-fatal) so the partner is
      //    credited for the lifetime of this account.
      await api.referral.submitSignup({
        code,
        email: email.trim().toLowerCase(),
        locale,
        utm,
      }).catch(() => {});
      // 2. Create the real account (Turnstile-protected). On signup the backend
      //    links the earliest referral_signups row by email → attribution set.
      await api.auth.signup(email.trim(), password, name.trim() || null, token);
      await reloadUser?.();
      navigate('/app', { replace: true });
    } catch (err) {
      if (err?.status === 403) { setError(tr.inviteOnly); }
      else { setError(err?.message || tr.errorGeneric); }
      setToken(null); // single-use Turnstile token
      setTsReset((n) => n + 1); // reset the widget so it mints a fresh token
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="rs-page">
        <div style={{ padding: '120px 0', textAlign: 'center', color: 'var(--text-muted)' }}>…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="rs-page">
        <div style={{ padding: '120px 24px', textAlign: 'center' }}>
          <h1 className="rs-h" style={{ fontSize: 32 }}>{tr.notFound}</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>
            <Link to="/">← Dathent</Link>
          </p>
        </div>
      </div>
    );
  }

  const grad = referrer?.avatarGradient || DEFAULT_GRADIENT;

  return (
    <div className="rs-page">
      <header className="rs-top">
        <Link to="/" className="rs-brand">
          <img src="/assets/dathent-logo.png" alt="" width={28} height={28} />
          <span>Dathent</span>
        </Link>
        <div className="rs-sp" />
        <span className="rs-have">
          {tr.have} <Link to="/login">{tr.signIn}</Link>
        </span>
        <select
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
          style={{
            background: 'transparent', color: 'var(--text-muted)',
            border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
            padding: '4px 8px', fontSize: 12, marginLeft: 12,
          }}
        >
          <option value="en">EN</option>
          <option value="uk">UK</option>
          <option value="ru">RU</option>
        </select>
        <div style={{ marginLeft: 8 }}>
          <ThemeToggle />
        </div>
      </header>

      <main className="rs-shell">
        <section className="rs-invite">
          <div className="rs-invite-bg" />
          <div className="rs-invite-inner">
            <span className="rs-eyebrow">
              <span className="rs-eyebrow-dot" />
              {tr.eyebrow}
            </span>

            <div className="rs-author">
              {referrer?.photoUrl ? (
                <img
                  className="rs-author-av"
                  src={referrer.photoUrl}
                  alt=""
                  style={{ objectPosition: referrer.photoPosition || '50% 50%' }}
                />
              ) : (
                <div
                  className="rs-author-av"
                  style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}
                >
                  {(referrer?.displayName || 'A').slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="rs-author-text">
                <div className="rs-author-name">{referrer?.displayName || t('referralSignup.friend')}</div>
                <div className="rs-author-studio">{referrer?.studio || ''}</div>
              </div>
            </div>

            <h1 className="rs-h">
              {referrer?.displayName || t('referralSignup.aFriend')} {tr.headPre} <span className="ac">{tr.headAccent}</span>
            </h1>

            {referrer?.customMessage && (
              <blockquote className="rs-message">
                {referrer.customMessage}
                <span className="rs-message-attr">{referrer.displayName}</span>
              </blockquote>
            )}

            <div className="rs-perks">
              {tr.perks.map((p, i) => (
                <div key={i} className="rs-perk">
                  <span className="rs-perk-ic">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      {i === 0 && <path d="M12 3 13.5 8 18 9.5 13.5 11 12 16 10.5 11 6 9.5 10.5 8z" />}
                      {i === 1 && (<><rect x="3" y="4.5" width="18" height="16.5" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></>)}
                      {i === 2 && (<><path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>)}
                    </svg>
                  </span>
                  <div>
                    <div className="rs-perk-l">{p.l}</div>
                    <div className="rs-perk-s">{p.s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="rs-form-wrap">
          <form className="rs-form" onSubmit={submit}>
            <span className="rs-bonus-pill">{tr.bonus}</span>

            <div className="rs-form-h">
              <h2>{tr.formTitle}</h2>
              <p>{tr.formSub}</p>
            </div>

            <div className="rs-field">
              <label className="rs-l">{tr.nameL}</label>
              <input
                className="rs-input"
                type="text"
                placeholder={tr.namePh}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
            </div>

            <div className="rs-field">
              <label className="rs-l">{tr.emailL}</label>
              <input
                className="rs-input"
                type="email"
                required
                placeholder={tr.emailPh}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            <div className="rs-field">
              <label className="rs-l">{tr.passwordL}</label>
              <input
                className="rs-input"
                type="password"
                required
                minLength={8}
                placeholder={tr.passwordPh}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            <Turnstile onVerify={setToken} resetSignal={tsReset} />

            {error && <div className="rs-error">{error}</div>}

            <button type="submit" className="btn btn-primary rs-cta" disabled={submitting || !token}>
              {submitting ? tr.submitting : (<>
                {tr.cta}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </>)}
            </button>

            <div className="rs-foot">
              {tr.foot[0]}<a href="#">{tr.foot[1]}</a>{tr.foot[2]}<a href="#">{tr.foot[3]}</a>{tr.foot[4]}
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

export default ReferralSignup;
